import { useContext, useRef, useState } from "react";
import Dashboard from "../Layout/Dashboard";
import ChatSidebar from "./ChatSidebar";
import ChatMessages from "./ChatMessages";
import { Toolbar } from "primereact/toolbar";
import { Button } from "primereact/button";
import { classNames } from "primereact/utils";
import FloatingButton from "../components/FloatingButton";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import {
  getChats,
  getSupportUsersForChat,
  getUsersForChat,
} from "../services/chat";
import { useLocation } from "react-router-dom";
import { NotificationBadgeContext } from "../NotificationBadgeContext";
import { useEffect } from "react";
import { requestFirebaseNotificationPermission } from "../firebaseMessaging";

export default function Chat() {
  const [showSidebar, setShowSidebar] = useState(true);
  const [selectedChat, setSelectedChat] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [showNotificationDialog, setShowNotificationDialog] = useState(false);
  const [userSearch, setUserSearch] = useState("");

  const location = useLocation();
  const user = location.state?.user;
  const [userForNewChat, setUserForNewChat] = useState(user ?? null);

  const [notifications, setNotifications] = useContext(
    NotificationBadgeContext
  );

  const usersForChat = useQuery({
    queryFn: () => getUsersForChat(userSearch),
    queryKey: ["usersForChat", { userName: userSearch }],
    enabled: userSearch.length >= 3,
  });

  const supportUsersForChat = useQuery({
    queryFn: () => getSupportUsersForChat("support"),
    queryKey: ["usersForChat", { support: true }],
  });

  const chats = useInfiniteQuery({
    queryFn: ({ pageParam = 1 }) => getChats({ page: pageParam }),
    queryKey: ["chats"],
    getNextPageParam: (lastPage, pages) => lastPage.nextPage,
  });

  const chatList = chats.data?.pages
    .map((page) => page.results)
    .reduce((acc, curr) => acc.concat(curr), []);

  const contentRef = useRef(null);
  const sidebarRef = useRef(null);

  useEffect(() => {
    const sidebarElem = sidebarRef.current;
    if (!sidebarElem) return;

    const handleScroll = () => {
      if (
        sidebarElem.scrollTop + sidebarElem.clientHeight >=
        sidebarElem.scrollHeight - 10
      ) {
        if (chats.hasNextPage && !chats.isFetchingNextPage) {
          chats.fetchNextPage();
        }
      }
    };

    sidebarElem.addEventListener("scroll", handleScroll);
    return () => sidebarElem.removeEventListener("scroll", handleScroll);
  }, [chats.hasNextPage, chats.isFetchingNextPage, chats]);

  useEffect(() => {
    const isAuthenticated = !!localStorage.getItem("token");
    if (isAuthenticated && window.location.pathname.includes("/chat")) {
      requestFirebaseNotificationPermission();
      if (Notification.permission !== "granted") {
        setShowNotificationDialog(true);
      } else {
        setShowNotificationDialog(false);
      }
    }
  }, []);

  return (
    <Dashboard headerTitle="Chat" withPannel={false}>
      {/* Solicita aceptar los permisos */}
      <Dialog
        draggable={false}
        visible={showNotificationDialog}
        onHide={() => setShowNotificationDialog(false)}
        header="Aceptar notificaciones 🔔"
      >
        <div className="px-4 pb-3">
          <p>
            Por favor, acepta los permisos de <b>notificaciones</b> para
            informarte sobre nuevos mensajes.
          </p>
          <p>Puedes hacerlo en la esquina superior izquierda</p>
          <div className="flex justify-content-end">
            <Button
              label="Listo"
              className="px-6"
              onClick={() => setShowNotificationDialog(false)}
            />
          </div>
        </div>
      </Dialog>

      {/* Vista principal */}
      <div className="grid">
        <div
          className={classNames("p-0", {
            "col-9": showSidebar,
            "col-12": !showSidebar,
          })}
        >
          <Toolbar
            className="border-noround py-3 chat-toolbar"
            start={
              selectedChat ? (
                <span className="font-semibold">{selectedChat.name}</span>
              ) : userForNewChat ? (
                <span className="font-semibold">
                  {userForNewChat.first_name} {userForNewChat.last_name}
                </span>
              ) : null
            }
            end={() => (
              <Button
                icon="pi pi-users"
                text
                onClick={() => setShowSidebar(!showSidebar)}
              />
            )}
          />
          <ChatMessages
            chatId={selectedChat?.id}
            userForNewChat={userForNewChat?.id}
            onChatCreated={(chat) => {
              setSelectedChat(chat);
              setUserForNewChat(null);
            }}
            contentRef={contentRef}
          />
        </div>
        <ChatSidebar
          isActive={showSidebar}
          contacts={chatList || []}
          supportContacts={supportUsersForChat.data || []}
          onClickContact={(contact) => {
            setSelectedChat(contact);
            if (notifications[contact.id]) {
              setNotifications((prev) => {
                const newState = { ...prev };
                delete newState[contact.id];
                localStorage.setItem(
                  "notificationState",
                  JSON.stringify(newState)
                );
                return newState;
              });
            }
            setUserForNewChat(null);
            if (contentRef.current)
              contentRef.current.scrollTop = contentRef.current.scrollHeight;
          }}
          onClickUser={(user) => {
            setUserForNewChat(user);
            setSelectedChat(null);
            if (contentRef.current)
              contentRef.current.scrollTop = contentRef.current.scrollHeight;
          }}
          selectedId={selectedChat?.id}
          isLoading={chats.isLoading || supportUsersForChat.isLoading}
        />
      </div>

      {/* Crear nuevo chat */}
      <FloatingButton
        icon="pi pi-plus"
        tooltip="Nuevo chat"
        onClick={() => setShowDialog(true)}
      />
      <Dialog
        visible={showDialog}
        header="Nuevo chat"
        draggable={false}
        onHide={() => {
          if (showDialog) setShowDialog(false);
        }}
        style={{ width: "30rem" }}
      >
        <div className="flex flex-column gap-3">
          <div className="p-field p-fluid">
            <label htmlFor="username">Usuario</label>
            <div className="mt-1 p-inputgroup flex-1">
              <InputText
                id="username"
                placeholder="Ingrese el usuario con el que desea comunicarse"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                autoComplete="off"
              />
              <span className="p-inputgroup-addon">
                <i className="pi pi-search"></i>
              </span>
            </div>
          </div>
          {usersForChat.isLoading ? (
            <div className="text-center">
              <i className="pi pi-spin pi-spinner"></i>
            </div>
          ) : usersForChat.isError ? (
            <div className="text-red-500 text-center">
              Error al cargar usuarios.
            </div>
          ) : (
            <ul className="list-none p-0 m-0">
              {usersForChat.data?.map((user) => (
                <li
                  key={user.id}
                  className="p-2 cursor-pointer hover:bg-gray-100 flex flex-column mb-1"
                  onClick={() => {
                    setUserForNewChat(user);
                    setShowDialog(false);
                    setUserSearch("");
                  }}
                >
                  <span className="font-semibold">
                    {user.first_name} {user.last_name}
                  </span>
                  <span className="text-sm">{user.username}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Dialog>
    </Dashboard>
  );
}
